import { AxiosInstance } from 'axios';
import type {
  PaymentMethod,
  Currency,
  Token,
  AccountPaymentConfig,
  Network,
} from '@sdk/shared/types/config.types';
import { handleServiceError } from '@sdk/utils/error-handler';
import { Auth } from '@sdk/auth';
import { Chain } from 'viem';
import { mapNetworkToIcon, mapTokenToIcon } from '@sdk/utils/icons';
import { IxChain } from '@shared/src/config/chains';
import { IxEventType } from '@sdk/notification/types';

interface DefaultConfig {
  defaultChainId: number;
  defaultCurrency: string;
  defaultToken: string;
}

const CURRENCY_FLAG_MAP: Record<string, string> = {
  GHS: '🇬🇭',
  KES: '🇰🇪',
  NGN: '🇳🇬',
  USD: '🇺🇸',
  EUR: '🇪🇺',
  GBP: '🇬🇧',
};

export class ConfigService {
  private publicAxios: AxiosInstance;
  private auth: Auth;
  public status: 'initialised' | 'loading' | { error: any } | 'not_initialised' = 'not_initialised';
  public chains: IxChain[];

  public quickTradeWaitTime: number = 1000 * 60 * 5; // 5 minutes

  public fetchQuickTradeWaitTime = async () => {
    const response = await this._getQuickTradeWait();
    return response.data;
  };

  public defaultChainId: number;
  public defaultCurrency: string;
  public defaultToken: string;

  networks: Network[] | null = null;
  currencies: Currency[] | null = null;
  tokens: Token[] | null = null;
  paymentMethods: PaymentMethod[] | null = null;
  private AccountPaymentConfigs: AccountPaymentConfig[] | null = null;

  private initPromise: Promise<boolean>;

  public async init(): Promise<boolean> {
    if (this.status === 'initialised' || this.status === 'loading') return false;
    this.status = 'loading';
    return new Promise(async (resolve, reject) => {
      (async () => {
        const [networks, currencies, tokens, paymentMethods, quickTradeWaitTime] =
          await Promise.all([
            this.fetchNetworks(),
            this.fetchCurrencies(),
            this.fetchTokens(),
            this.fetchPaymentMethods(),
            this._getQuickTradeWait(),
          ]);

        this.networks = networks.data;
        this.currencies = currencies.data;
        this.tokens = tokens.data;
        this.paymentMethods = paymentMethods.data;
        if (quickTradeWaitTime.data) this.quickTradeWaitTime = quickTradeWaitTime.data;
        if (!this.networks || !this.currencies || !this.tokens || !this.paymentMethods) {
          console.error({
            networks,
            currencies,
            tokens,
            paymentMethods,
          });
          throw new Error('unable to inittialise');
        }
        this.status = 'initialised';
        this.auth.notification.notify({
          type: IxEventType.INITIALIZED,
          data: {
            currencies: this.currencies,
            paymentMethods: this.paymentMethods,
            networks: this.networks,
            tokens: this.tokens,
            quickTradeWaitTime: this.quickTradeWaitTime,
          },
        });
        console.log('ConfigService initialized');
        resolve(true);
      })().catch(err => {
        this.status = {
          error: err,
        };
        reject(err);
      });
    });
  }

  public waitForInit = async (): Promise<boolean> => {
    if (this.status === 'initialised') return true;
    return await this.initPromise;
  };

  constructor(auth: Auth, defaultConfig: DefaultConfig) {
    this.publicAxios = auth.publicAxios;
    this.defaultChainId = defaultConfig.defaultChainId;
    this.defaultCurrency = defaultConfig.defaultCurrency;
    this.defaultToken = defaultConfig.defaultToken;
    this.auth = auth;
    this.chains = auth.chains;
    this.initPromise = this.init();
  }

  // Public endpoints (no auth required)
  fetchNetworks = async () => {
    return handleServiceError(async () => {
      const { data }: { data: Network[] } = await this.publicAxios.get<Network[]>(
        '/configs/networks',
      );
      if (!data) throw new Error('Failed to fetch networks');
      return data.map(network => mapNetworkToIcon(network));
    }, []);
  };

  fetchPaymentMethods = async () => {
    return handleServiceError(async () => {
      const { data } = await this.publicAxios.get<PaymentMethod[]>('/configs/payment-methods');
      if (!data) throw new Error('Failed to fetch payment methods');
      return data;
    }, []);
  };

  fetchCurrencies = async () => {
    return handleServiceError(async () => {
      const { data } = await this.publicAxios.get<Currency[]>('/configs/currencies');
      if (!data) throw new Error('Failed to fetch currencies');
      return data.map(currency => ({
        ...currency,
        icon: CURRENCY_FLAG_MAP[currency.symbol] ?? currency.icon,
      }));
    }, []);
  };

  fetchTokens = async (networkId?: number) => {
    return handleServiceError(async () => {
      const { data } = await this.publicAxios.get<Token[]>('/configs/tokens', {
        params: networkId ? { networkId } : undefined,
      });
      if (!data) throw new Error('Failed to fetch tokens');
      return data.map(token => mapTokenToIcon(token));
    }, []);
  };

  // Protected endpoints (auth required)
  getAccountPaymentConfigs = async ({ forceRefresh = false }: { forceRefresh?: boolean } = {}) => {
    if (!this.AccountPaymentConfigs || forceRefresh) {
      const { data } = await this.fetchAccountPaymentConfigs();
      this.AccountPaymentConfigs = data;
    }
    return this.AccountPaymentConfigs;
  };

  fetchAccountPaymentConfigs = async () => {
    return handleServiceError(async () => {
      const { data } = await this.auth.protectedAxios.get<AccountPaymentConfig[]>(
        '/configs/payment/configs',
      );
      if (!data) throw new Error('Failed to fetch payment configs');
      return data.filter(config => config.isActive);
    }, []);
  };

  createPaymentConfig = async (payload: {
    account: string;
    accountName: string;
    methodId: string;
  }) => {
    return handleServiceError(async () => {
      const { data } = await this.auth.protectedAxios.post<AccountPaymentConfig>(
        '/configs/payment/config',
        payload,
      );
      if (!data) throw new Error('Failed to create payment config');
      return data;
    });
  };

  updatePaymentConfig = async (
    configId: number,
    payload: { account: string; accountName: string; extraInfo?: string; methodId: string },
  ) => {
    return handleServiceError(async () => {
      const { data } = await this.auth.protectedAxios.post<AccountPaymentConfig>(
        `/configs/payment/config/${configId}`,
        payload,
      );
      if (!data) throw new Error('Failed to update payment config');
      return data;
    });
  };

  deletePaymentConfig = async (configId: number) => {
    return handleServiceError(async () => {
      const { data } = await this.auth.protectedAxios.post<AccountPaymentConfig>(
        `/configs/payment/config/${configId}`,
        {
          isActive: false,
        },
      );
      if (!data) throw new Error('Failed to delete payment config');
      return data;
    });
  };

  private _getQuickTradeWait = async () => {
    return handleServiceError(async () => {
      const { data } = await this.publicAxios.get<number>('/quick-trade/wait');
      if (!data) throw new Error('Failed to fetch quick trade wait time');
      return data;
    });
  };
}
